import StepCardAction from '@/pages/Run/components/StepCardActions.tsx';
import StepExecutionLogs from '@/pages/Run/components/StepExecutionLogs/StepExecutionLogs.tsx';
import RunStepCard from '@/pages/Run/components/RunStepCard/RunStepCard.tsx';
import { StepType } from '@/types/automationFields.ts';
import ExtractResultsRenderer from '@/pages/Run/components/ExtractResults/ExtractResultsRenderer.tsx';
import { AutomationStep } from '@/types/automationSteps.ts';
import React, { useState } from 'react';
import StepIterationsRenderer from '@/pages/Run/components/StepIterationsRenderer.tsx';
import FailureHandlerOutcomeNarrative from '@/pages/Run/components/FailureHandlerOutcomeNarrative.tsx';
import { Divider } from '@mantine/core';
import {
  ParentStepRun,
  RetainedPriorScopeStepRun,
  StepRun,
  WaitingStepDependency,
} from '@/types/stepExecutions.ts';
import { Run } from '@/types/runs.ts';
import { isStepWithChildren, StepDisabledInfo } from '@/utils/stepUtils.ts';
import StepRunConnectionsPanel from '@/pages/Run/components/StepRunConnectionsPanel.tsx';
import ChildScopeLink, {
  ChildScopeContextReporter,
} from '@/pages/Run/components/ScopeStack/ChildScopeLink.tsx';
import { ChildScopeContext } from '@/pages/Run/components/ScopeStack/childScopeModel.ts';
import { AutoExpandPlan } from '@/types/autoExpandPlan.ts';
import {
  HistoricalScopePresentationProvider,
  useIsHistoricalScopePresentation,
} from '@/pages/Run/components/HistoricalScopePresentationContext.ts';
import { getTerminalReasonPresentation } from '@/utils/terminalReasonPresentation.ts';

type Props = {
  stepRuns: StepRun[];
  retainedPriorScope?: RetainedPriorScopeStepRun;
  run: Run;
  step: AutomationStep;
  index: number;
  parentSteps: ParentStepRun[];
  allowHistoricalStepRuns?: boolean;
  stepsById: Record<string, AutomationStep>;
  disabledInfo?: StepDisabledInfo;
  waitingDependencies: WaitingStepDependency[];
  childScopePresentation?: 'inline' | 'horizontal';
  onOpenChildScope?: (context: ChildScopeContext, isHistorical?: boolean) => void;
  onChildScopeContextChange?: (context: ChildScopeContext) => void;
  activeScopeParentStepRunId?: string;
  autoExpandPlan?: AutoExpandPlan | null;
};

const StepResultsCard = ({
  run,
  step,
  stepRuns,
  retainedPriorScope,
  stepsById,
  index,
  parentSteps,
  allowHistoricalStepRuns = false,
  disabledInfo,
  waitingDependencies,
  childScopePresentation = 'inline',
  onOpenChildScope,
  onChildScopeContextChange,
  activeScopeParentStepRunId,
  autoExpandPlan,
}: Props) => {
  const [showReferences, setShowReferences] = useState(false);
  const isHistoricalScopePresentation = useIsHistoricalScopePresentation();

  return (
    <RunStepCard
      run={run}
      step={step}
      stepRuns={stepRuns}
      retainedPriorScope={retainedPriorScope}
      index={index}
      parentSteps={parentSteps}
      allowHistoricalStepRuns={allowHistoricalStepRuns}
      disabledInfo={disabledInfo}
      waitingDependencies={waitingDependencies}
      stepsById={stepsById}
      autoExpandPlan={autoExpandPlan}
      activeScopeParentStepRunId={activeScopeParentStepRunId}
      topLabelPlacement={childScopePresentation === 'horizontal' ? 'inside' : 'above'}
    >
      {({ isOpen, stepRun, stepExecution, isRetainedPriorScope }) => {
        const childScopeIsHistorical =
          isHistoricalScopePresentation ||
          (isRetainedPriorScope && !stepRun?.rerun_eligibility?.allowed);
        const childScopeContext =
          stepExecution && stepRun
            ? { step, stepRun, stepExecution, parentSteps }
            : null;

        return (
          <>
            {childScopeContext && onChildScopeContextChange && isStepWithChildren(step) && (
              <ChildScopeContextReporter
                context={childScopeContext}
                onContextChange={onChildScopeContextChange}
              />
            )}
            {isOpen && stepExecution && step.step_type === StepType.FailureHandler && (
              <FailureHandlerOutcomeNarrative stepExecution={stepExecution} />
            )}
            <StepExecutionLogs
              logItemId={stepExecution?.id}
              stepRunId={stepRun?.id}
              enabled={isOpen}
              stepStatus={stepExecution?.status}
              terminalReasonPresentation={getTerminalReasonPresentation(
                stepExecution,
                stepRun?.full_path,
              )}
            />
            <StepCardAction
              stepExecution={stepExecution}
              stepRun={stepRun}
              run={run}
              step={step}
              stepsById={stepsById}
              showReferences={showReferences}
              onToggleReferences={() => setShowReferences((value) => !value)}
              rerunDisabledMessage={
                isHistoricalScopePresentation
                  ? 'Rerunning prior results is not supported yet.'
                  : stepRun?.rerun_eligibility?.allowed === false
                    ? (stepRun.rerun_eligibility.reason ?? 'This result cannot be rerun.')
                    : isRetainedPriorScope && !stepRun?.rerun_eligibility?.allowed
                      ? 'Refresh the run to check whether this retained result can be rerun.'
                      : undefined
              }
            />
            {showReferences && (
              <StepRunConnectionsPanel
                stepRun={stepRun}
                stepsById={stepsById}
                enabled={isOpen && !!stepRun}
              />
            )}

            {isOpen &&
              stepExecution &&
              stepRun &&
              (step.step_type === StepType.ExtractData || isStepWithChildren(step)) && (
              <>
                {step.step_type === StepType.ExtractData && (
                  <>
                    <Divider />
                    <HistoricalScopePresentationProvider value={childScopeIsHistorical}>
                      <ExtractResultsRenderer
                        stepExecution={stepExecution}
                        run={run}
                        stepRun={stepRun}
                      />
                    </HistoricalScopePresentationProvider>
                  </>
                )}
                {isStepWithChildren(step) &&
                  (childScopePresentation === 'horizontal' &&
                  onOpenChildScope &&
                  childScopeContext ? (
                    <HistoricalScopePresentationProvider value={childScopeIsHistorical}>
                      <ChildScopeLink
                        run={run}
                        context={childScopeContext}
                        stepsById={stepsById}
                        onOpen={onOpenChildScope}
                        autoExpandPlan={autoExpandPlan}
                      />
                    </HistoricalScopePresentationProvider>
                  ) : (
                    <HistoricalScopePresentationProvider
                      value={childScopeIsHistorical}
                    >
                      <StepIterationsRenderer
                        run={run}
                        stepRun={stepRun}
                        stepExecution={stepExecution}
                        parentSteps={[
                          ...parentSteps,
                          { ...stepRun, step_execution: stepExecution },
                        ]}
                        stepsById={stepsById}
                        autoExpandPlan={autoExpandPlan}
                      />
                    </HistoricalScopePresentationProvider>
                  ))}
              </>
            )}
          </>
        );
      }}
    </RunStepCard>
  );
};

export default StepResultsCard;
